import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, euros } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { name: "", kind: "relay", price: 0, description: "", active: true, order: 0 };

export default function DeliveryAdmin() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/delivery-methods").then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...editing, price: Number(editing.price) || 0, order: Number(editing.order) || 0 };
      if (editing.id) await api.put(`/admin/delivery-methods/${editing.id}`, payload);
      else await api.post("/admin/delivery-methods", payload);
      toast.success("Mode de livraison enregistré");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce mode de livraison ?")) return;
    await api.delete(`/admin/delivery-methods/${id}`);
    toast.success("Supprimé");
    load();
  };

  return (
    <div data-testid="admin-delivery">
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight uppercase text-onyx">Livraison</h1>
        <button onClick={() => setEditing({ ...EMPTY })} data-testid="delivery-add"
          className="inline-flex items-center gap-2 bg-rouge text-white px-5 py-2.5 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors">
          <Plus className="w-4 h-4" /> Ajouter
        </button>
      </div>

      {!items ? <p className="text-muted-foreground">Chargement…</p> : (
        <div className="space-y-3" data-testid="delivery-list">
          {items.map((m) => (
            <div key={m.id} className="bg-white border border-line p-5 flex items-center gap-4 flex-wrap" data-testid={`delivery-row-${m.id}`}>
              <Truck className="w-5 h-5 text-rouge shrink-0" />
              <div className="flex-1 min-w-[200px]">
                <p className="font-display font-bold text-onyx">{m.name} {!m.active && <span className="text-xs font-sans font-semibold text-muted-foreground">(désactivé)</span>}</p>
                <p className="text-sm text-muted-foreground">{m.description}</p>
              </div>
              <p className="font-display font-bold text-onyx">{m.price === 0 ? "Gratuit" : euros(m.price)}</p>
              <button onClick={() => setEditing({ ...m })} data-testid={`delivery-edit-${m.id}`} className="p-2 text-onyx hover:text-rouge" aria-label="Modifier"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => remove(m.id)} data-testid={`delivery-delete-${m.id}`} className="p-2 text-onyx hover:text-destructive" aria-label="Supprimer"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-white" data-testid="delivery-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing?.id ? "Modifier" : "Ajouter"} un mode de livraison</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label>Nom</Label>
                <Input data-testid="delivery-name-input" className="mt-1.5" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div>
                <Label>Type de remise</Label>
                <select data-testid="delivery-kind-select" className="mt-1.5 w-full border border-input bg-white px-3 py-2 text-sm"
                  value={editing.kind || "relay"} onChange={(e) => setEditing({ ...editing, kind: e.target.value })}>
                  <option value="pickup">Retrait chez un partenaire (gratuit)</option>
                  <option value="relay">Point relais</option>
                  <option value="home">Livraison à domicile</option>
                </select>
              </div>
              <div>
                <Label>Prix (€) — 0 = gratuit / retrait</Label>
                <Input type="number" step="0.01" min="0" data-testid="delivery-price-input" className="mt-1.5" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} />
              </div>
              <div>
                <Label>Description</Label>
                <Input data-testid="delivery-description-input" className="mt-1.5" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
              <div className="flex items-center gap-3">
                <Switch data-testid="delivery-active-switch" checked={!!editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
                <Label>Actif (visible sur la page Acheter)</Label>
              </div>
              <button onClick={save} disabled={saving || !editing.name} data-testid="delivery-save"
                className="w-full bg-rouge text-white px-6 py-3 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover transition-colors disabled:opacity-50">
                {saving ? "Enregistrement…" : "Enregistrer"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
